import React, {useMemo} from 'react'
import styled from 'styled-components'
import {AVATAR_SIZE_CHAT} from 'consts'
import {translate} from 'i18n'
import {
  FirebaseMessageRoomItem,
  FirebaseMessageRoomMemberItem,
  FirebaseMessageRoomType,
} from 'types'
import {useFirebaseSystem, useHistory, useMessageContent} from 'utils'
import {Avatar, Badge, Paragraph} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'

const StyledContainer = styled.div`
  flex-direction: row;
  display: flex;
  align-items: center;
  padding: ${convertUnit(16)} ${convertUnit(20)};
  cursor: pointer;
`
const StyledHeader = styled.div`
  flex-direction: row;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
`
const StyledUserData = styled.div`
  flex-direction: column;
  display: flex;
  flex: 1;
`
const StyledDateTime = styled.div`
  margin-left: ${convertUnit(10)};
`
const StyledMessagePreview = styled(Paragraph)`
  height: ${convertUnit(15)};
  overflow: hidden;
  text-overflow: ellipsis;
`
const StyledUsername = styled.div`
  flex-direction: row;
  display: flex;
  align-items: center;
`
const StyledAvatar = styled(Avatar)`
  aspect-ratio: 1;
  margin-right: ${convertUnit(15)};
`
const StyledMessageContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  word-wrap: break-word;
  margin-top: ${convertUnit(4)};
`
const StyledChatIndicator = styled.div`
  width: ${convertUnit(24)};
  height: ${convertUnit(24)};
  border-radius: ${convertUnit(24)};
  background-color: ${({theme}) => theme.warning_5};
`

export interface GiftShopMessageListItemSystemProps {
  members: FirebaseMessageRoomMemberItem[]
  messages: FirebaseMessageRoomItem
  roomId: string
  roomType: FirebaseMessageRoomType
  hasNewMessage: boolean
  search: string
}
export default function GiftShopMessageListItemSystem({
  members,
  messages,
  roomId,
  roomType,
  hasNewMessage,
  search,
}: GiftShopMessageListItemSystemProps) {
  const {id} = useSelector('user')!
  const history = useHistory()
  const participant = useMemo(() => members.find((item) => item.id !== id), [
    id,
    members,
  ])
  const {data} = useFirebaseSystem({id: participant?.id || ''})
  const {photo = '', system} = data || {}
  const {description, time} = useMessageContent({message: messages})
  const name = useMemo(() => translate('messages:system', {context: system}), [
    system,
  ])

  const isValid = useMemo(
    () => name.toLowerCase().includes(search.toLowerCase()),
    [name, search],
  )

  return participant && data && isValid ? (
    <StyledContainer
      onClick={() =>
        history.push('giftshop_message_room', {
          participantId: participant.id,
          roomType,
          roomId,
          roomName: name,
          roomPhoto: photo,
        })
      }>
      <StyledAvatar
        containerStyle={{marginRight: 15}}
        src={photo}
        alt="Avatar"
        size={AVATAR_SIZE_CHAT}
      />
      <StyledUserData>
        <StyledHeader>
          <StyledUsername>
            <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
              {name}
            </Paragraph>
            <Badge
              text={translate('messages:badgeBot')}
              color="white_1"
              backgroundColor="success_5"
              isMessageBadge
            />
          </StyledUsername>
          <StyledDateTime>
            <Paragraph color="gray_3">{time}</Paragraph>
          </StyledDateTime>
        </StyledHeader>
        <StyledMessageContainer>
          <StyledMessagePreview>{description}</StyledMessagePreview>
          {hasNewMessage && <StyledChatIndicator />}
        </StyledMessageContainer>
      </StyledUserData>
    </StyledContainer>
  ) : null
}
